import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { EnchancedModal } from 'src/components/Modals';
import { MultiSelectFilter } from 'src/components/Search/components';
import type { TSelectItem } from 'src/components/Search/components/types';
import { useWarboxConversation } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import useStore from 'src/hooks/useStore';
import { SnackbarAlert } from 'src/components/Alerts/components/System/components/SnackbarAlert';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import { getStoreTags } from '../../utils/storeTags';
import type { ConversationTag } from '../../types';

type TAddConversationTagsModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    conversationId?: string;
    storeId?: string;
    currentTags?: ConversationTag[];
};

type TTagOption = ConversationTag & { label: string; value: string };

const getTagId = (tag: ConversationTag | { id?: string; _id?: string }) =>
    String(tag.id || (tag as { _id?: string })._id || '');

const toSelectItems = (tags: ConversationTag[] = []): TSelectItem[] =>
    tags
        .map((tag) => {
            const id = getTagId(tag);
            if (!id) return null;
            return { label: `${tag.icon} ${tag.name}`, value: id };
        })
        .filter((item): item is TSelectItem => Boolean(item));

export const AddConversationTagsModal = ({
    open,
    setOpen,
    conversationId,
    storeId,
    currentTags = [],
}: TAddConversationTagsModalProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { store, stores, getStore, getStores } = useStore();
    const { updateConversation } = useWarboxConversation();
    const { customAlert } = SnackbarAlert();
    const [selectedTags, setSelectedTags] = useState<TSelectItem[]>([]);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (!open) return;

        setSelectedTags(toSelectItems(currentTags));

        if (storeId) {
            getStore(storeId);
            return;
        }

        getStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, storeId, currentTags]);

    const storeForTags = useMemo(() => {
        if (storeId && store && getDocumentId(store) === storeId) {
            return store;
        }

        const fromUserStores = (user?.stores || []).find(
            (item: any) => getDocumentId(item) === storeId
        );
        if (fromUserStores) return fromUserStores;

        const fromStores = (stores || []).find(
            (item: any) => getDocumentId(item) === storeId
        );
        if (fromStores) return fromStores;

        return null;
    }, [store, storeId, stores, user?.stores]);

    const tagCatalog = useMemo((): TTagOption[] => {
        return getStoreTags(storeForTags)
            .filter((tag) => tag._id && tag.name?.trim())
            .map((tag) => ({
                id: tag._id as string,
                name: tag.name,
                color: tag.color,
                icon: tag.icon,
                label: tag.name,
                value: tag._id as string,
            }));
    }, [storeForTags]);

    const tagOptions = useMemo(
        () =>
            tagCatalog.map(({ label, value, icon }) => ({
                label: `${icon} ${label}`,
                value,
                icon,
            })),
        [tagCatalog]
    );

    const handleUpdate = async () => {
        if (!conversationId || isUpdating) return;

        setIsUpdating(true);
        try {
            const currentById = new Map(
                currentTags.map((tag) => [getTagId(tag), tag])
            );
            const catalogById = new Map(tagCatalog.map((tag) => [tag.id, tag]));

            const nextTags: ConversationTag[] = selectedTags
                .map((selected) => {
                    const fromCatalog = catalogById.get(selected.value);

                    if (fromCatalog) {
                        return {
                            id: fromCatalog.id,
                            name: fromCatalog.name,
                            color: fromCatalog.color,
                            icon: fromCatalog.icon,
                        };
                    }

                    const fromCurrent = currentById.get(selected.value);

                    if (fromCurrent) {
                        return {
                            id: getTagId(fromCurrent),
                            name: fromCurrent.name,
                            color: fromCurrent.color,
                            icon: fromCurrent.icon,
                        };
                    }

                    return null;
                })
                .filter((tag): tag is ConversationTag => Boolean(tag));

            await updateConversation({ tags: nextTags }, conversationId);
            customAlert(t('SnackBar.ConversationUpdated'), 'success');
            setOpen(false);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <EnchancedModal
            open={open}
            setOpen={setOpen}
            title={t('Chat.AddTag')}
            maxWidth="xs"
        >
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
                <Typography variant="body2" color="textSecondary">
                    {t('Chat.FilterByTags')}
                </Typography>

                <MultiSelectFilter
                    label={t('Stores.Tags.Title')}
                    options={tagOptions}
                    value={selectedTags}
                    onChange={(value) =>
                        setSelectedTags((value as TSelectItem[]) || [])
                    }
                    placeholder={t('Chat.FilterByTags')}
                    isClearable
                    isDisabled={isUpdating || tagOptions.length === 0}
                />

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={isUpdating}
                    >
                        {t('Buttons.Update')}
                    </Button>
                </Box>
            </Box>
        </EnchancedModal>
    );
};
